/**
 * @file   src\Pages\SignIn.tsx
 * @brief  SignIn page.
 * @date   JUL, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */

import { React, Button, Link, useNavigate, useState } from '../utils/thirdpartyComponents';
import Carwashlogo from '../assets/images/CarWash-Logo.svg';
import CWInput from '../components/CWInput';
import '../styles/signin.scss';
import { CommonMessages } from '../messages/validationMessages';
import { pageURLs } from '../utils/pageUrls';
import { MessageToaster } from '../utils/toastUtil';
import { AuthApi } from '../utils/apiUrls';
import axios from '../config/axiosConfig';
import { userType } from '../utils/enums';
import FullLoader from '../components/FullLoader';

const ForgotPasswordStep2 = (props: any) => {

    // Initialize states for otp
    const [otp, setOtp] = useState(["", "", "", ""]);

    // Toast object creation.
    const toaster = new MessageToaster();

    // Initialize state to manage errors
    const [error, setError] = useState<string | null>(null)
    const [loading, setLoading] = useState(false);

    const navigate = useNavigate()

    // Triggers whenever input is entered
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {

        // Clear error
        setError(null)

        // Extract only digits from the input value
        const value = e.target.value.replace(/\D/g, '');

        // Check the length of the value to only accept one number
        if (value.length === 1) {
            if (value) {
                // Update the OTP state with the new value at the specified index
                setOtp(prevOtp => {
                    const newOtp = [...prevOtp];
                    newOtp[index] = value;
                    return newOtp;
                });

                // Focus on the next input field if it exists
                if (index < otp.length - 1) {
                    const nextInput = document.getElementById(`otpinput${index + 2}`);
                    if (nextInput) {
                        (nextInput as HTMLInputElement).focus();
                    }
                }
            }
        }
        else {
            // spiting value to get new entered value
            const oldvalue = value.toString().split('');
            const newvalue = oldvalue[oldvalue.length - 1];
            if (newvalue) {
                const newnumbervalue = newvalue.replace(/\D/g, '');
                // Focus on the next input field if it exists
                if (index < otp.length - 1) {
                    const nextInput = document.getElementById(`otpinput${index + 2}`);
                    if (nextInput) {
                        (nextInput as HTMLInputElement).focus();
                    }
                }
                // check any value found in current index
                if (otp[index] === "") {
                    // if current index value is empty add new value in current index
                    setOtp(prevOtp => {
                        const newOtp = [...prevOtp];
                        newOtp[index] = newnumbervalue;
                        return newOtp;
                    });
                }
                else {
                    // if current index value is not empty set focus to next index
                    const nextInput = document.getElementById(`otpinput${index + 3}`);
                    if (nextInput) {
                        (nextInput as HTMLInputElement).focus();
                    }
                    // check the otp limit
                    if (index < otp.length - 1) {
                        // add value in next index
                        setOtp(prevOtp => {
                            const newOtp = [...prevOtp];
                            newOtp[index + 1] = newnumbervalue;
                            return newOtp;
                        });
                    }
                }

                // Focus on the next input field if it exists
                if (index < otp.length - 1) {
                    const nextInput = document.getElementById(`otpinput${index + 2}`);
                    if (nextInput) {
                        (nextInput as HTMLInputElement).focus();
                    }
                }
            }
        }
    };



    const resendOTP = async () => {
        setLoading(true)
        // e.preventDefault()
        // Validate form data
        // await FORGOTPASSWORD_STEP1_SCHEMA.validate(formData, { abortEarly: false });
        if (localStorage.getItem('forgotemail')) {
            const formData = {
                email: localStorage.getItem('forgotemail'),
                adminUserType: props.userType
            }
            const { data } = await axios.post(AuthApi.FORGOT_PASSWORD, formData);
            if (data?.error == 0) {
                setOtp(["", "", "", ""])
                // Navigate to otp verification page
                // navigate(`/superadmin/${pageURLs.forgotpassword_step2}`, { state: {'email':formData.email}})
            }
            else {
                // show error message
                toaster.toastError(data?.message);
            }
        }



        setLoading(false)
    }


    // Triggers whenever handle key
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
        if (e.key === "Enter") {
            handleSubmit();
        } else if (e.key === "Backspace" || e.key === "Delete") {
            e.preventDefault(); // Prevent default behavior

            // If current OTP input is empty, move focus to the previous input
            if (otp[index] === "") {
                if (index > 0) {
                    const prevInput = document.getElementById(`otpinput${index}`) as HTMLInputElement;
                    if (prevInput) {
                        prevInput.focus();
                    }
                }
            } else {
                // Clear the current OTP input if it's not empty
                setOtp(prevOtp => {
                    const newOtp = [...prevOtp];
                    newOtp[index] = "";
                    return newOtp;
                });

                // Move focus to the current input
                const currentInput = document.getElementById(`otpinput${index}`) as HTMLInputElement;
                if (currentInput) {
                    currentInput.focus();
                }
            }
        }
    };

    // Triggers when verification button click
    const handleSubmit = async () => {

        // Check if OTP is entered
        if (otp.includes("")) {
            setError(CommonMessages.otp_required)
        } else {
            try {
                setLoading(true)
                //join the otp values
                const enteredOtp = otp.join("");
                const requestData = {
                    email: localStorage.getItem('forgotemail'),
                    adminUserType: props.userType,
                    otp: parseInt(enteredOtp)
                }
                const { data } = await axios.post(AuthApi.RESET_PASSWORD_TOKEN_VALIDATION, requestData);
                if (data?.error == 0) {
                    if (props.userType == userType['SUPER-ADMIN'])
                        navigate(`/superadmin/${pageURLs.forgotpassword_step3}`, { state: { resetToken: data?.data?.resetToken } })
                    else
                        navigate(`/${pageURLs.forgotpassword_step3}`, { state: { resetToken: data?.data?.resetToken } })
                }
                else {
                    setLoading(false)
                    // show error message
                    toaster.toastError(data?.message);
                }
            } catch (error: any) {
                throw new Error(error?.response?.data?.message || error?.message);
            }

        }
    };

    return (
        <div>
            <div className="signin-bg"></div>
            <div className="signin-wrap d-flex">
                <div className="signin-container d-grid">
                    <div className="signin-hero d-flex align-items-start flex-column justify-content-center">
                        <img src={Carwashlogo} alt="Car Wash App" />
                        <h1>{props.userType === userType['SUPER-ADMIN'] ? 'Super Admin' : 'Car Wash Owner'}</h1>
                    </div>
                    <div className="signin-form-wrap password-form-wrap">
                        <h2>Forgot Password</h2>
                        <p className='sub'>Please enter the verification code received on your Email to verify your account.</p>
                        <div className="code-group">
                            <p>Enter Code here</p>
                            <div className="code-input d-grid">
                                {otp.map((value, index) => (
                                    < CWInput
                                        key={index}
                                        id={`otpinput${index + 1}`}
                                        autoFocus={index === 0}
                                        name="otpinput"
                                        type="text"
                                        pattern="\d*" // Restrict to digits only
                                        errorMessage=""
                                        maxLength={2}
                                        value={value}
                                        onChange={(e) => handleChange(e, index)}
                                        onKeyPress={(e) => handleKeyDown(e, index)}
                                    />
                                ))}
                            </div>
                        </div>

                        <div className='resend'><Link onClick={() => resendOTP()} to={""} title="Resend code" className='f-bold'>Resend code</Link></div>
                        {error ? <span className="error-msg d-flex mb-2">{error}</span> : null}
                        <Button
                            title='Validate And Reset Password'
                            type="submit"
                            variant="primary"
                            className="primary-btn"
                            onClick={handleSubmit}
                        >
                            Validate And Reset Password
                        </Button>

                        <div className="new-user text-center">Back to  <Link to={`/superadmin/${pageURLs.root}`} title="Sign In" >Sign In</Link></div>

                    </div>

                </div>

            </div>
            {loading && <FullLoader />}
        </div>
    )
}

export default ForgotPasswordStep2;
